import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  ColumnConfiguration,
  TableConfiguration,
  TableVariant,
  TABLE_VARIANT,
} from '../constants/columnConfigurationsConstants';

type UseConfigurationActionsProps = {
  configuration: TableConfiguration;
  saveConfiguration: (
    config: { columnConfigurations: ColumnConfiguration[]; variant: TableVariant },
    signal?: AbortSignal,
  ) => Promise<Response>;
  revertConfiguration: (variant: TableVariant, signal?: AbortSignal) => Promise<Response>;
  refetchQueryKey: QueryKey;
};

export const useConfigurationActions = ({
  configuration,
  refetchQueryKey,
  saveConfiguration,
  revertConfiguration,
}: UseConfigurationActionsProps) => {
  const queryClient = useQueryClient();
  const { mutate: updateVariant } = useMutation({
    mutationFn: async (variant: TableVariant) => {
      return saveConfiguration({
        variant,
        columnConfigurations:
          variant === TABLE_VARIANT.Flat
            ? configuration.flatColumnConfigurations
            : configuration.combinedColumnConfigurations,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: refetchQueryKey });
    },
  });

  const { mutate: revertToDefault } = useMutation({
    mutationFn: () => {
      if (configuration?.enabledVariant === undefined) {
        throw new Error('need to fetch configuration first!');
      }

      return revertConfiguration(configuration.enabledVariant);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: refetchQueryKey });
    },
  });

  return {
    configuration,
    updateVariant,
    revertToDefault,
  };
};
